/**
 * Chart documentation shown behind the info icon on the Campaigns dashboard.
 *
 * Keys match the table link property names in `MopaCampaignsComponent`.
 */
export const MopaCampaignsChartDocs = {
    mastertable: /* HTML */ `<p>
            Acquisition campaigns with their cost, the revenue of the players
            they brought, and the ratios between the two.
        </p>
        <p>
            Player KPIs use first-open cohorts; AppsFlyer metrics use reporting
            dates. Cross-source ratios aggregate source-only rows; they are not
            per-row matches. Income, net/gross and the currency follow their
            toggles.
        </p>
        <ul>
            <li><strong>Costs</strong> - campaign spend. AppsFlyer.</li>
            <li>
                <strong>Income</strong> - revenue from the cohort's players.
                BIEvents.
            </li>
            <li>
                <strong>Reattribution income</strong> - revenue from players
                credited to the campaign by reattribution. BIEvents, but read
                <strong>without the valid-player filter</strong> that Income
                uses, so the two columns do not cover the same population.
            </li>
            <li>
                <strong>Total ROAS</strong> - aggregated income / costs;
                <strong>Total Profit</strong> - aggregated income - costs.
            </li>
            <li>
                <strong>Ad revenue share</strong> - ad revenue / total revenue.
                BIEvents.
            </li>
            <li>
                <strong>Impressions</strong> - ads bought in the campaign.
                AppsFlyer.
            </li>
            <li><strong>Installs</strong> - attributed installs. AppsFlyer.</li>
            <li><strong>Transactions</strong> - purchases. BIEvents.</li>
            <li>
                <strong>Payers</strong>, <strong>Payers D7</strong> - players
                with a purchase, ever and within 7 days. BIEvents.
            </li>
            <li>
                <strong>Reattributions</strong> - returning players credited to
                the campaign, not fresh installs. AppsFlyer.
            </li>
            <li>
                <strong>CPI</strong> - costs / installs; <strong>CPM</strong> -
                costs x 1000 / impressions; <strong>IPM</strong> - installs x
                1000 / impressions. All AppsFlyer on both sides.
            </li>
            <li>
                <strong>CPT</strong> - costs / transactions;
                <strong>CPPU</strong> - costs / payers;
                <strong>CPPU D7</strong> - costs / payers D7. Cost from
                AppsFlyer, the denominator from BIEvents.
            </li>
        </ul>
        <p>
            The remaining columns repeat per day number of the cohort's life
            (and, for ROAS, at weekly and monthly milestones):
        </p>
        <ul>
            <li>
                <strong>Revenue DN</strong> - revenue on that day;
                <strong>Running Revenue DN</strong> - summed up to that day.
            </li>
            <li>
                <strong>ROAS DN / WN / MN</strong> - running revenue to that day
                / campaign cost.
            </li>
            <li>
                <strong>ARPU DN</strong> - running revenue to that day / cohort
                size, where cohort size is the count of <strong>day-0 players
                    from BIEvents</strong>, deliberately not AppsFlyer installs,
                so it matches ARPU on the other dashboards.
            </li>
            <li>
                <strong>CPT DN</strong> - running sum of daily cost /
                transactions.
            </li>
            <li>
                <strong>Retention DN</strong> - players active on that day /
                cohort size.
            </li>
        </ul>
        <p>
            Day-number values end at the latest first-open date; recent cohorts
            leave late columns empty rather than showing a low number.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_campaigns</code> (campaign KPIs;
            player KPIs are valid-only except reattribution revenue);
            <code>bi_viz.dashboard_sales</code> (day-number revenue and
            transactions);
            <code>bi_stg.player_biviz_dims</code> (cohort size, valid players
            only); <code>bi_viz.dashboard_users_activity</code> (retention).
        </p>`,
} as const;
